import { useState, useEffect, useRef } from "react";

// ============================================================
// BINARY GAME: CONTENT APPROVAL TREE
// Client: AI Learning Labs (AILL)
// Domain: Content Operations / Approval Flow
// Route: /sales/aill-content-approval
// ============================================================

// Status badges for gates
const STATUS = {
  LIVE: { label: "LIVE", color: "#16a34a", bg: "#f0fdf4" },
  NEEDS_DECISION: { label: "NEEDS DECISION", color: "#d97706", bg: "#fffbeb" },
};

const TREE_DEFINITION = {
  title: "Content Approval Tree",
  subtitle: "Binary Game Decision Tree // AILL // 2026",
  intro: {
    heading: "Should this content ship?",
    body: "Every piece of content that is not groceries, gas, or rent runs through this tree. It sorts the content type, checks the quality gates, and tells you whether it ships, needs revision, or gets held. The tree replaces the bottleneck of one person approving everything.",
    cta: "Start the tree",
  },

  gates: {
    gate1: {
      id: "gate1",
      type: "three-way",
      label: "GATE 01",
      status: STATUS.LIVE,
      question: "What kind of content is this?",
      subtitle: "If it does not fit a category, it does not enter the tree. Park it or kill it.",
      options: [
        {
          label: "FOUNDATIONAL",
          description: "Website, playbook, curriculum, team page. Lives a long time. High bar.",
        },
        {
          label: "CAMPAIGN",
          description: "Ads, email sequences, webinar pages, ManyChat. Connected to money. Medium bar.",
        },
        {
          label: "DAILY",
          description: "Social posts, reels, stories. Fast, high volume. Lower bar.",
        },
      ],
    },
    gate2: {
      id: "gate2",
      type: "yes-no",
      label: "GATE 02",
      status: STATUS.NEEDS_DECISION,
      question: "Has it been run through the optimization checklist?",
      subtitle: "The checklist includes: Is the overlay right? Is the format optimized? Have we checked best practices? Does the team have a hypothesis for why this approach?",
      prerequisite: "Nathaniel's optimization document + Gem. Not built yet.",
      optionA: { label: "YES" },
      optionB: { label: "NO" },
      exitText: "Send it back. The team runs it through the Gem/document first. It does not advance until the checklist is complete.",
    },
    gate3: {
      id: "gate3",
      type: "yes-no",
      label: "GATE 03",
      status: STATUS.NEEDS_DECISION,
      question: "Does it match the visual template for this content type?",
      subtitle: "Once templates exist, this becomes a simple check: does this look like one of our approved formats? Andrea is the authority here. If she says it matches, it matches.",
      prerequisite: "Andrea's visual templates locked. In progress. Not complete.",
      note: "Exception: sometimes the right move is NOT to use the brand style (e.g., UGC testimonials should look organic, not branded). The template set should include a 'no brand' template for those cases.",
      optionA: { label: "YES" },
      optionB: { label: "NO" },
      exitText: "Send to Andrea for visual correction.",
    },
    gate4: {
      id: "gate4",
      type: "yes-no",
      label: "GATE 04",
      status: STATUS.NEEDS_DECISION,
      question: "Has it exceeded the revision cap for this content type?",
      subtitle: "If it has hit the max number of revision rounds, it ships (unless a hold exception applies at Gate 6).",
      prerequisite: "Team agrees on revision limits per category.",
      caps: [
        { category: "Foundational", value: "TBD" },
        { category: "Campaign", value: "TBD" },
        { category: "Daily", value: "TBD" },
      ],
      optionA: { label: "YES, CAP HIT" },
      optionB: { label: "NO, ROUNDS LEFT" },
      exitText: "One more round allowed. Return to the reviewer.",
    },
    gate5: {
      id: "gate5",
      type: "yes-no",
      label: "GATE 05",
      status: STATUS.NEEDS_DECISION,
      question: "Has it exceeded the time cap for this content type?",
      subtitle: "If it has been in the pipeline longer than the max allowed time, it ships (unless a hold exception applies at Gate 6). Current lead time for social is 3-4 weeks, which is likely too long.",
      prerequisite: "Team agrees on time limits per category.",
      caps: [
        { category: "Foundational", value: "TBD" },
        { category: "Campaign", value: "TBD" },
        { category: "Daily", value: "TBD (currently 3-4 weeks)" },
      ],
      optionA: { label: "YES, TIME'S UP" },
      optionB: { label: "NO, STILL IN WINDOW" },
      exitText: "Continue working within the time window.",
    },
    gate6: {
      id: "gate6",
      type: "yes-no",
      label: "GATE 06",
      status: STATUS.LIVE,
      question: "Does a hold exception apply?",
      subtitle: "From the session: brand misrepresentation, active campaign conflict, and factual errors are not real risks right now.",
      holdExceptions: [
        "Factual error (wrong name, wrong number, wrong link). Easy to catch with a proof pass.",
        "Nathaniel's tone check on content representing him personally. Gets a time limit: TBD hours to respond or it ships.",
        "Any others? Should be no more than 3 total.",
      ],
      optionA: { label: "YES, HOLD" },
      optionB: { label: "NO, SHIP IT" },
      exitText: "Fix the specific issue within 24 hours. Then re-enter at Gate 5.",
    },
    gate7: {
      id: "gate7",
      type: "yes-no",
      label: "GATE 07",
      status: STATUS.LIVE,
      question: "Who approved this?",
      subtitle: "Has the right person for this content category signed off?",
      approvalLayers: [
        { category: "Foundational", approvers: "Nathaniel + Andrea" },
        { category: "Campaign", approvers: "Alexis + Andrea (needs Alexis confirmation)" },
        { category: "Daily", approvers: "Ellie self-publishes with Andrea's visual sign-off. Nathaniel does NOT review unless it represents him personally." },
      ],
      optionA: { label: "YES, APPROVED" },
      optionB: { label: "NO" },
      exitText: "Route to the correct approver based on content category.",
    },
  },

  resolveSequence: (answers) => {
    const seq = ["gate1"];
    if (!answers["gate1"]) return seq;

    // Gate 1 just sorts the content type. All paths continue to gate 2.
    seq.push("gate2");
    if (!answers["gate2"]) return seq;
    if (answers["gate2"] === "NO") return seq; // Exit: send back

    seq.push("gate3");
    if (!answers["gate3"]) return seq;
    if (answers["gate3"] === "NO") return seq; // Exit: send to Andrea

    seq.push("gate4");
    if (!answers["gate4"]) return seq;

    // If cap hit, skip to gate 6 (hold check) then gate 7
    if (answers["gate4"] === "YES, CAP HIT") {
      seq.push("gate6");
      if (!answers["gate6"]) return seq;
      if (answers["gate6"] === "YES, HOLD") return seq; // Exit: hold and fix
      seq.push("gate7");
      return seq;
    }

    // If rounds left, continue to gate 5
    seq.push("gate5");
    if (!answers["gate5"]) return seq;
    if (answers["gate5"] === "NO, STILL IN WINDOW") return seq; // Exit: keep working

    // Time's up, check for hold exception
    seq.push("gate6");
    if (!answers["gate6"]) return seq;
    if (answers["gate6"] === "YES, HOLD") return seq; // Exit: hold and fix

    seq.push("gate7");
    return seq;
  },
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#F5F3EF",
    fontFamily: "'Work Sans', -apple-system, sans-serif",
    color: "#1a1a1a",
  },
  container: {
    maxWidth: 760,
    margin: "0 auto",
    padding: "40px 20px 80px",
  },
  header: { marginBottom: 48 },
  headerMeta: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 11, fontWeight: 600, letterSpacing: "0.1em",
    color: "#999", textTransform: "uppercase", marginBottom: 12,
  },
  headerTitle: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 36, fontWeight: 700, color: "#1a1a1a",
    lineHeight: 1.2, marginBottom: 8,
  },
  headerSubtitle: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12, color: "#999", letterSpacing: "0.04em",
  },
  divider: { width: "100%", height: 1, background: "#e0ddd8", margin: "32px 0" },
  introCard: {
    background: "#fff", borderLeft: "4px solid #2563EB",
    borderRadius: 2, padding: "28px 32px",
  },
  introHeading: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 22, fontWeight: 700, marginBottom: 16, color: "#1a1a1a",
  },
  introBody: { fontSize: 16, lineHeight: 1.6, color: "#444", marginBottom: 24 },
  gateCard: (isActive, isResolved) => ({
    background: isResolved ? "#fafaf8" : "#fff",
    borderLeft: `4px solid ${isResolved ? "#ccc" : "#2563EB"}`,
    borderRadius: 2, padding: "28px 32px",
    opacity: isResolved ? 0.6 : 1,
    transition: "opacity 0.3s ease, background 0.3s ease",
    pointerEvents: isResolved ? "none" : "auto",
  }),
  gateMeta: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 11, fontWeight: 600, letterSpacing: "0.08em",
    color: "#999", marginBottom: 8, textTransform: "uppercase",
    display: "flex", alignItems: "center", gap: 10,
  },
  statusBadge: (status) => ({
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
    color: status.color, background: status.bg,
    padding: "3px 8px", borderRadius: 3,
  }),
  gateQuestion: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 22, fontWeight: 700, lineHeight: 1.4,
    color: "#1a1a1a", marginBottom: 8,
  },
  gateSubtitle: { fontSize: 15, color: "#777", lineHeight: 1.5, marginBottom: 16 },
  prerequisiteBox: {
    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 4,
    padding: "12px 16px", marginBottom: 16,
  },
  prerequisiteLabel: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
    color: "#d97706", textTransform: "uppercase", marginBottom: 4,
  },
  prerequisiteText: { fontSize: 13, color: "#92400e", lineHeight: 1.5 },
  noteBox: {
    background: "#f0f4ff", border: "1px solid #dbeafe", borderRadius: 4,
    padding: "12px 16px", marginBottom: 16,
  },
  noteLabel: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
    color: "#2563EB", textTransform: "uppercase", marginBottom: 4,
  },
  noteText: { fontSize: 13, color: "#1e40af", lineHeight: 1.5 },
  capsTable: {
    width: "100%", borderCollapse: "collapse", marginBottom: 16,
  },
  capRow: {
    display: "flex", justifyContent: "space-between",
    padding: "8px 0", borderBottom: "1px solid #eee",
  },
  capCategory: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12, fontWeight: 600, color: "#444",
  },
  capValue: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12, fontWeight: 700, color: "#d97706",
  },
  optionBtn: (isChosen, isUnchosen) => ({
    display: "block", width: "100%", textAlign: "left",
    padding: "16px 20px", marginBottom: 10,
    border: isChosen ? "2px solid #2563EB" : isUnchosen ? "2px solid #e5e5e5" : "2px solid #ddd",
    borderRadius: 4,
    background: isChosen ? "#EFF4FF" : isUnchosen ? "#f9f9f7" : "#fff",
    cursor: isUnchosen ? "default" : "pointer",
    opacity: isUnchosen ? 0.4 : 1,
    transition: "all 0.2s ease",
  }),
  optionLabel: (isChosen) => ({
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 13, fontWeight: 700, letterSpacing: "0.04em",
    color: isChosen ? "#2563EB" : "#1a1a1a", marginBottom: 4,
  }),
  optionDesc: { fontSize: 13, color: "#777", lineHeight: 1.5 },
  simpleBtn: (isChosen, isUnchosen) => ({
    display: "inline-block",
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 13, fontWeight: 700, letterSpacing: "0.04em",
    padding: "14px 28px", marginRight: 12, marginBottom: 8,
    borderRadius: 4, border: "2px solid transparent",
    cursor: isUnchosen ? "default" : "pointer",
    opacity: isUnchosen ? 0.4 : 1,
    background: isChosen ? "#2563EB" : "#fff",
    color: isChosen ? "#fff" : "#1a1a1a",
    borderColor: isChosen ? "#2563EB" : "#ddd",
    transition: "all 0.15s ease",
  }),
  connector: {
    display: "flex", flexDirection: "column",
    alignItems: "flex-start", paddingLeft: 18, gap: 0,
  },
  connectorLine: { width: 3, background: "#2563EB", borderRadius: 1 },
  connectorLabel: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12, fontWeight: 700, color: "#2563EB",
    padding: "2px 0", marginLeft: 10,
  },
  exitCard: {
    background: "#1a1a1a", borderRadius: 4, padding: "28px 28px",
    color: "#fff", animation: "fadeSlideIn 0.5s ease-out",
  },
  exitBadge: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
    color: "#f59e0b", textTransform: "uppercase", marginBottom: 12,
  },
  exitText: { fontSize: 16, lineHeight: 1.7, color: "#ccc" },
  shipCard: {
    background: "#1a1a1a", borderRadius: 4, padding: "32px 28px",
    color: "#fff", animation: "fadeSlideIn 0.5s ease-out",
    textAlign: "center",
  },
  shipBadge: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
    color: "#16a34a", textTransform: "uppercase", marginBottom: 12,
  },
  shipText: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 28, fontWeight: 700, color: "#fff",
  },
  contentTypeBadge: {
    display: "inline-block",
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
    color: "#2563EB", background: "#EFF4FF",
    padding: "4px 10px", borderRadius: 3, marginBottom: 16,
  },
  resetBtn: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12, fontWeight: 600, letterSpacing: "0.06em",
    color: "#999", background: "none", border: "1px solid #ddd",
    borderRadius: 4, padding: "10px 20px", cursor: "pointer",
    marginTop: 40, display: "block",
  },
};

// ============================================================
// COMPONENTS
// ============================================================

function Connector({ label }) {
  return (
    <div style={styles.connector}>
      <div style={{ ...styles.connectorLine, height: 24 }} />
      {label && <span style={styles.connectorLabel}>{label}</span>}
      <div style={{ ...styles.connectorLine, height: 24 }} />
    </div>
  );
}

function GateCard({ gate, isActive, isResolved, chosenAnswer, onAnswer, contentType }) {
  const ref = useRef(null);

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isActive]);

  const isThreeWay = gate.type === "three-way";

  return (
    <div ref={ref} style={styles.gateCard(isActive, isResolved)}>
      <div style={styles.gateMeta}>
        <span>{gate.label} | {gate.type === "three-way" ? "THIS / THAT" : "YES / NO"}</span>
        {gate.status && (
          <span style={styles.statusBadge(gate.status)}>{gate.status.label}</span>
        )}
      </div>

      {contentType && isActive && (
        <div style={styles.contentTypeBadge}>CONTENT TYPE: {contentType}</div>
      )}

      <div style={styles.gateQuestion}>{gate.question}</div>
      {gate.subtitle && <div style={styles.gateSubtitle}>{gate.subtitle}</div>}

      {gate.prerequisite && (
        <div style={styles.prerequisiteBox}>
          <div style={styles.prerequisiteLabel}>Prerequisite</div>
          <div style={styles.prerequisiteText}>{gate.prerequisite}</div>
        </div>
      )}

      {gate.note && (
        <div style={styles.noteBox}>
          <div style={styles.noteLabel}>Note</div>
          <div style={styles.noteText}>{gate.note}</div>
        </div>
      )}

      {gate.caps && (
        <div style={{ marginBottom: 16 }}>
          {gate.caps.map((cap, i) => (
            <div key={i} style={styles.capRow}>
              <span style={styles.capCategory}>{cap.category}</span>
              <span style={styles.capValue}>{cap.value}</span>
            </div>
          ))}
        </div>
      )}

      {gate.holdExceptions && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...styles.prerequisiteLabel, color: "#666", marginBottom: 8 }}>
            Hold exceptions
          </div>
          {gate.holdExceptions.map((ex, i) => (
            <div key={i} style={{ fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 6, paddingLeft: 12 }}>
              {"\u25C6"} {ex}
            </div>
          ))}
        </div>
      )}

      {gate.approvalLayers && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...styles.prerequisiteLabel, color: "#666", marginBottom: 8 }}>
            Approval layers
          </div>
          {gate.approvalLayers.map((layer, i) => (
            <div key={i} style={styles.capRow}>
              <span style={styles.capCategory}>{layer.category}</span>
              <span style={{ ...styles.capValue, color: "#444", fontWeight: 500, maxWidth: "60%", textAlign: "right" }}>{layer.approvers}</span>
            </div>
          ))}
        </div>
      )}

      {isThreeWay ? (
        <div>
          {gate.options.map((opt) => {
            const isChosen = chosenAnswer === opt.label;
            const isUnchosen = chosenAnswer && !isChosen;
            return (
              <button
                key={opt.label}
                style={styles.optionBtn(isChosen, isUnchosen)}
                onClick={() => !isResolved && onAnswer(opt.label)}
              >
                <div style={styles.optionLabel(isChosen)}>{opt.label}</div>
                {opt.description && <div style={styles.optionDesc}>{opt.description}</div>}
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          {[gate.optionA, gate.optionB].map((opt) => {
            const isChosen = chosenAnswer === opt.label;
            const isUnchosen = chosenAnswer && !isChosen;
            return (
              <button
                key={opt.label}
                style={styles.simpleBtn(isChosen, isUnchosen)}
                onClick={() => !isResolved && onAnswer(opt.label)}
              >
                {isChosen ? "\u25C6 " : ""}{opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function ContentApprovalTree() {
  const [started, setStarted] = useState(false);
  const [currentGateIndex, setCurrentGateIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const sequence = TREE_DEFINITION.resolveSequence(answers);

  const handleAnswer = (gateId, answer) => {
    const gateIndex = sequence.indexOf(gateId);
    if (gateIndex !== currentGateIndex) return;

    const newAnswers = { ...answers, [gateId]: answer };
    setAnswers(newAnswers);
    setCurrentGateIndex((prev) => prev + 1);
  };

  const reset = () => {
    setStarted(false);
    setCurrentGateIndex(0);
    setAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Determine content type from Gate 1
  const contentType = answers["gate1"] || null;

  // Determine if tree is complete
  const lastGateId = sequence[sequence.length - 1];
  const lastGate = TREE_DEFINITION.gates[lastGateId];
  const lastAnswer = answers[lastGateId];

  // Tree ends in an exit if the last answered gate has an exitText and the answer triggers it
  const isExitState = (() => {
    if (!lastAnswer) return false;
    if (lastGateId === "gate2" && lastAnswer === "NO") return true;
    if (lastGateId === "gate3" && lastAnswer === "NO") return true;
    if (lastGateId === "gate4" && lastAnswer === "NO, ROUNDS LEFT") return true;
    if (lastGateId === "gate5" && lastAnswer === "NO, STILL IN WINDOW") return true;
    if (lastGateId === "gate6" && lastAnswer === "YES, HOLD") return true;
    if (lastGateId === "gate7" && lastAnswer === "NO") return true;
    return false;
  })();

  // Tree ships if gate7 is answered YES
  const isShipState = lastGateId === "gate7" && lastAnswer === "YES, APPROVED";

  // Show exit/result when currentGateIndex has moved past the last gate in sequence
  const showResult = currentGateIndex >= sequence.length && (isExitState || isShipState);

  const getExitText = () => {
    if (!lastGate) return "";
    if (isShipState) return null;
    return lastGate.exitText || "";
  };

  return (
    <div style={styles.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Work+Sans:wght@400;500;600&display=swap');
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        button { font-family: inherit; }
        @media (max-width: 768px) {
          .cat-container { padding: 24px 16px 60px !important; }
        }
      `}</style>

      <div className="cat-container" style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerMeta}>
            {"\u25C6"} Binary Game / Content Operations
          </div>
          <div style={styles.headerTitle}>Content Approval Tree</div>
          <div style={styles.headerSubtitle}>
            Binary Game Decision Tree // AI Learning Labs // 2026
          </div>
        </div>

        <div style={styles.divider} />

        {!started ? (
          <div style={{ animation: "fadeSlideIn 0.5s ease-out" }}>
            <div style={styles.introCard}>
              <div style={styles.introHeading}>{TREE_DEFINITION.intro.heading}</div>
              <div style={styles.introBody}>{TREE_DEFINITION.intro.body}</div>
              <button
                style={{
                  ...styles.simpleBtn(false, false),
                  background: "#2563EB", color: "#fff", borderColor: "#2563EB",
                }}
                onClick={() => setStarted(true)}
              >
                {"\u25C6"} {TREE_DEFINITION.intro.cta}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {sequence.map((gateId, index) => {
              const gate = TREE_DEFINITION.gates[gateId];
              if (!gate) return null;
              if (index > currentGateIndex) return null;

              const isActive = index === currentGateIndex;
              const isResolved = index < currentGateIndex;

              return (
                <div
                  key={gate.id}
                  style={{ animation: isActive ? "fadeSlideIn 0.4s ease-out" : "none" }}
                >
                  {index > 0 && (
                    <Connector
                      label={isResolved || isActive ? answers[sequence[index - 1]] : null}
                    />
                  )}
                  <GateCard
                    gate={gate}
                    isActive={isActive}
                    isResolved={isResolved}
                    chosenAnswer={answers[gate.id]}
                    onAnswer={(answer) => handleAnswer(gate.id, answer)}
                    contentType={contentType}
                  />
                </div>
              );
            })}

            {/* Exit result */}
            {showResult && !isShipState && (
              <div>
                <Connector label={lastAnswer} />
                <div style={styles.exitCard}>
                  <div style={styles.exitBadge}>ACTION REQUIRED</div>
                  <div style={styles.exitText}>{getExitText()}</div>
                </div>
              </div>
            )}

            {/* Ship result */}
            {showResult && isShipState && (
              <div>
                <Connector label={lastAnswer} />
                <div style={styles.shipCard}>
                  <div style={styles.shipBadge}>APPROVED</div>
                  <div style={styles.shipText}>Ship it.</div>
                  {contentType && (
                    <div style={{ ...styles.contentTypeBadge, marginTop: 16 }}>
                      {contentType}
                    </div>
                  )}
                </div>
              </div>
            )}

            {showResult && (
              <button style={styles.resetBtn} onClick={reset}>
                RUN AGAIN
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
